import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";
import * as Sentry from "@sentry/react";

import { OnboardingRoutes, MinimalRoutes, AuthenticatedRoutes } from "./Routes";
import OnboardingLayout from "@/layouts/OnboardingLayout";
import MinimalLayout from "@/layouts/MinimalLayout";
import AuthenticatedLayout from "@/layouts/AuthenticatedLayout";
import ErrorBoundary from "@/layouts/ErrorBoundary";
import ProtectedRoute from "@/router/ProtectedRoute";
import NavigationBlocker from "@/components_v2/Guards/NavigationBlocker.js";
import { AnalyticsTracker } from "@/libraries/analytics";

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

const wrapWithSentryErrorBoundary = (Component, props = {}) => (
  <Sentry.ErrorBoundary fallback={<ErrorBoundary />}>
    <Component {...props} />
  </Sentry.ErrorBoundary>
);

const RootWrapper = () => (
  <>
    <NavigationBlocker />
    <AnalyticsTracker />
    <Outlet />
  </>
);

const mapRoutes = (routes, wrapLeaf) =>
  routes.map((route) => {
    const segment = route.index ? { index: true } : { path: route.path };

    if (route.children) {
      return {
        ...segment,
        element: <Outlet />,
        children: mapRoutes(route.children, wrapLeaf),
      };
    }

    return { ...segment, element: wrapLeaf(route) };
  });

const wrapPublicLeaf = (route) =>
  wrapWithSentryErrorBoundary(route.view, route?.props);

const wrapProtectedLeaf = (route) => (
  <ProtectedRoute
    element={wrapWithSentryErrorBoundary(route.view, route?.props)}
    roles={route.allowedRoles || []}
    organizationRoles={route.allowedOrgRoles || []}
    requiredCapability={route.requiredCapability}
  />
);

const router = sentryCreateBrowserRouter([
  {
    element: <RootWrapper />,
    children: [
      {
        element: wrapWithSentryErrorBoundary(OnboardingLayout),
        children: mapRoutes(OnboardingRoutes, wrapPublicLeaf),
      },
      {
        element: wrapWithSentryErrorBoundary(MinimalLayout),
        children: mapRoutes(MinimalRoutes, wrapPublicLeaf),
      },
      {
        element: wrapWithSentryErrorBoundary(AuthenticatedLayout),
        children: mapRoutes(AuthenticatedRoutes, wrapProtectedLeaf),
      },
      { path: "*", element: <Navigate to="/login" /> },
    ],
  },
]);

export default router;
